import {
  createFileRoute,
  redirect,
  useRouteContext,
  useRouter,
} from "@tanstack/react-router";
import { useRef, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import { GoogleCalendarPageQueryDocument } from "@repo/graphql-types/graphql";
import Left from "@repo/icons/default/Left";

import { GoogleCalendarConnect } from "@/components/google-calendar/google-calendar-connect";
import { GoogleCalendarConnected } from "@/components/google-calendar/google-calendar-connected";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { ConfirmationDrawer } from "@/components/ui/drawer/confirmation-drawer";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useErrorDrawer } from "@/hooks/use-error-drawer";
import {
  typedQueryFn,
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { openGoogleAuthorization } from "@/lib/google-calendar/open-google-authorization";
import { trackEvent } from "@/lib/tracking";
import { logError } from "@/lib/utils/log-error";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const GoogleCalendarPageQuery = graphql(/* GraphQL */ `
  query GoogleCalendarPageQuery {
    googleCalendarStatus {
      conectado
      googleEmail
      dataUltimaSync
      webhookAtivo
    }
  }
`);

const GoogleCalendarAuthUrlQuery = graphql(/* GraphQL */ `
  query GoogleCalendarAuthUrlQuery {
    googleCalendarAuthUrl {
      url
    }
  }
`);

const DesconectarGoogleCalendarMutation = graphql(/* GraphQL */ `
  mutation DesconectarGoogleCalendarMutation {
    desconectarGoogleCalendar {
      desconectado
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

const DISCONNECT_DRAWER_PARAM = "disconnect-google-calendar";

const googleCalendarSearch = z.object({
  action: z.literal(DISCONNECT_DRAWER_PARAM).optional().catch(undefined),
});

const GoogleCalendarPage = (): JSX.Element => {
  const router = useRouter();
  const { authClient, user } = useRouteContext({ from: "__root__" });
  const { showErrorDrawer } = useErrorDrawer();
  // Evita toque duplo sem trocar o visual do botão: a URL chega rápido demais
  // para um estado de carregando, que só piscaria na tela.
  const isConnecting = useRef(false);

  const statusQueryResult = useGraphQL(GoogleCalendarPageQuery);
  const status = statusQueryResult.data?.googleCalendarStatus;
  const invalidateStatus = useInvalidateQuery(GoogleCalendarPageQueryDocument);

  const { mutateAsync: disconnect, isPending: isDisconnecting } =
    useGraphQLMutationWithErrorHandler(DesconectarGoogleCalendarMutation);

  const handleConnect = async (): Promise<void> => {
    if (isConnecting.current) {
      return;
    }

    isConnecting.current = true;
    trackEvent("Google Agenda Conexao Iniciada", { codUsuario: user.codUsuario });

    try {
      const data = await typedQueryFn(GoogleCalendarAuthUrlQuery, authClient);
      const url = data.googleCalendarAuthUrl?.url;

      if (!url) {
        throw new Error("googleCalendarAuthUrl sem url");
      }

      await openGoogleAuthorization(url);
    } catch (error) {
      logError(error);
      showErrorDrawer();
    } finally {
      // No app o Browser abre por cima e esta tela continua montada.
      isConnecting.current = false;
    }
  };

  const openDisconnectDrawer = (): void => {
    void router.navigate({ to: ".", search: { action: DISCONNECT_DRAWER_PARAM } });
  };

  const handleDisconnect = async (): Promise<void> => {
    try {
      await disconnect({});
      trackEvent("Google Agenda Desconectado", { codUsuario: user.codUsuario });
      invalidateStatus();
      router.history.back();
    } catch {
      // O useGraphQLMutationWithErrorHandler já exibe o drawer de erro.
    }
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Google Agenda" />
      </HeaderRoot>
      <Page className="p-6">
        <AsyncDataWrapper {...statusQueryResult}>
          {status?.conectado ? (
            <GoogleCalendarConnected
              googleEmail={status.googleEmail}
              dataUltimaSync={status.dataUltimaSync}
              webhookAtivo={status.webhookAtivo}
              onDisconnect={openDisconnectDrawer}
            />
          ) : (
            <GoogleCalendarConnect onConnect={() => void handleConnect()} />
          )}
        </AsyncDataWrapper>
      </Page>
      <ConfirmationDrawer
        openParam={DISCONNECT_DRAWER_PARAM}
        title="Desconectar o Google Agenda?"
        message="Seus compromissos do Google deixam de fechar horários na Livance, e a Livance perde o acesso à sua agenda. Você pode conectar de novo quando quiser."
        confirmText="Desconectar"
        onConfirm={() => void handleDisconnect()}
        isPending={isDisconnecting}
      />
    </>
  );
};

export const Route = createFileRoute("/settings/google-calendar/")({
  validateSearch: googleCalendarSearch,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-integracao-google-agenda"]) {
      redirect({ to: "/settings", throw: true });
    }
  },
  component: GoogleCalendarPage,
});
